import { clsx } from "clsx";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import { Text } from "../typography";

export interface PaginationProps {
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  /** Pages shown on either side of the current page. Default 1. */
  siblingCount?: number;
  className?: string;
  "aria-label"?: string;
}

type PageItem = number | "ellipsis-left" | "ellipsis-right";

function buildPages(page: number, pageCount: number, siblingCount: number): PageItem[] {
  if (pageCount <= 1) return [1];

  const firstPage = 1;
  const lastPage = pageCount;
  const leftSibling = Math.max(page - siblingCount, firstPage);
  const rightSibling = Math.min(page + siblingCount, lastPage);

  const showLeftEllipsis = leftSibling > firstPage + 1;
  const showRightEllipsis = rightSibling < lastPage - 1;

  const items: PageItem[] = [firstPage];
  if (showLeftEllipsis) items.push("ellipsis-left");
  else for (let i = firstPage + 1; i < leftSibling; i++) items.push(i);

  for (let i = leftSibling; i <= rightSibling; i++) {
    if (i !== firstPage && i !== lastPage) items.push(i);
  }

  if (showRightEllipsis) items.push("ellipsis-right");
  else for (let i = rightSibling + 1; i < lastPage; i++) items.push(i);

  if (lastPage !== firstPage) items.push(lastPage);
  return items;
}

export function Pagination({
  page,
  pageCount,
  onPageChange,
  siblingCount = 1,
  className,
  "aria-label": ariaLabel = "Pagination",
}: PaginationProps) {
  const items = buildPages(page, pageCount, siblingCount);
  const canPrev = page > 1;
  const canNext = page < pageCount;

  return (
    <nav aria-label={ariaLabel} className={clsx("inline-flex items-center gap-1", className)}>
      <PageButton
        disabled={!canPrev}
        onClick={() => canPrev && onPageChange(page - 1)}
        aria-label="Previous page"
      >
        <IconChevronLeft size={16} />
      </PageButton>

      {items.map((item, idx) =>
        item === "ellipsis-left" || item === "ellipsis-right" ? (
          <span
            key={`${item}-${idx}`}
            aria-hidden
            className="inline-flex size-8 items-center justify-center"
          >
            <Text variant="bodyXs" inline tone="body">…</Text>
          </span>
        ) : (
          <PageButton
            key={item}
            selected={item === page}
            onClick={() => onPageChange(item)}
            aria-label={`Go to page ${item}`}
            aria-current={item === page ? "page" : undefined}
          >
            <Text
              variant="bodySmBold"
              inline
              className={item === page ? "text-textOnDark" : "text-textBody"}
            >
              {item}
            </Text>
          </PageButton>
        ),
      )}

      <PageButton
        disabled={!canNext}
        onClick={() => canNext && onPageChange(page + 1)}
        aria-label="Next page"
      >
        <IconChevronRight size={16} />
      </PageButton>
    </nav>
  );
}

type PageButtonProps = {
  children: React.ReactNode;
  onClick?: () => void;
  selected?: boolean;
  disabled?: boolean;
  "aria-label"?: string;
  "aria-current"?: "page" | undefined;
};

function PageButton({
  children,
  onClick,
  selected = false,
  disabled = false,
  "aria-label": ariaLabel,
  "aria-current": ariaCurrent,
}: PageButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-current={ariaCurrent}
      className={clsx(
        "inline-flex size-8 items-center justify-center rounded-md transition-colors",
        selected
          ? "bg-brandBlack text-textOnDark"
          : "border border-borderDefault bg-bgWhite text-textBody hover:bg-bgLight",
        disabled && "cursor-not-allowed opacity-40",
      )}
    >
      {children}
    </button>
  );
}
